// The managed-order lifecycle — one ordered set of 10 stages the trader tracks and
// the admin drives. requests.managed_status holds the current stage's canonical
// value; the tracker maps any historical status onto a stage index.
//
// `customs` and `out_for_delivery` were split out of what used to be a single
// "shipping + clearance" stage, so the trader can see the order sitting in
// customs separately from it being on a local van. Nothing in the database
// constrains these values — requests.managed_status and managed_events.stage
// have no CHECK constraint — so adding a stage is a code-only change HERE.
//
// It is NOT code-only in the outbox: wa_managed_stage_index() (a plpgsql
// function) hardcodes this same ordered list for the forward-only rule, and a
// stage missing from it returns 0, which wa_on_managed_event reads as "not a
// lifecycle stage" and silently sends nothing. Any change to the list below
// needs a matching migration — see 20260914050717 for `confirmed` — plus
// MANAGED_STAGE_ORDER / MANAGED_STAGE_TEMPLATE in whatsapp-send/templates.ts.

export const MANAGED_STAGES = [
  { key: 'received',         ar: 'استلمنا طلبك',      en: 'Received',          zh: '已收到' },
  { key: 'sourcing',         ar: 'بحثنا وتفاوضنا',    en: 'Sourcing',          zh: '寻源谈判' },
  { key: 'offer',            ar: 'عرضك جاهز',         en: 'Offer ready',       zh: '报价就绪' },
  // Buyer-driven, never an admin click: telr-verify writes it when the trader
  // pays the offer. Deliberately excluded from the admin stage advancer.
  { key: 'confirmed',        ar: 'تم الاعتماد',        en: 'Confirmed',         zh: '已确认' },
  { key: 'production',       ar: 'قيد الإنتاج',        en: 'In production',     zh: '生产中' },
  { key: 'video',            ar: 'فيديو قبل الشحن',    en: 'Pre-ship video',    zh: '发货前视频' },
  { key: 'shipping',         ar: 'في الشحن',           en: 'Shipping',          zh: '运输中' },
  { key: 'customs',          ar: 'التخليص الجمركي',    en: 'Customs clearance', zh: '清关中' },
  { key: 'out_for_delivery', ar: 'خرجت للتوصيل',       en: 'Out for delivery',  zh: '派送中' },
  { key: 'delivered',        ar: 'وصل إليك',           en: 'Delivered',         zh: '已送达' },
];

// Every managed_status value (past + present) → its stage key.
const STATUS_TO_STAGE = {
  submitted: 'received', admin_review: 'received',
  sourcing: 'sourcing', matching: 'sourcing', shortlist_ready: 'sourcing',
  buyer_review: 'sourcing', negotiation: 'sourcing', buyer_selected: 'sourcing',
  offer_ready: 'offer',
  confirmed: 'confirmed',
  // Historical values stay mapped to production ON PURPOSE. Orders paid before
  // `confirmed` existed never had a confirmed stage, and remapping them would
  // move live orders BACKWARDS in the trader's progress bar. New payments write
  // 'confirmed' (telr-verify); these three are what came before.
  approved: 'production', deposit_paid: 'production', in_production: 'production',
  video_review: 'video',
  shipping: 'shipping',
  customs: 'customs',
  out_for_delivery: 'out_for_delivery',
  delivered: 'delivered', completed: 'delivered',
};

// The canonical managed_status the admin writes when advancing to a stage.
export const STATUS_FOR_STAGE = {
  received: 'admin_review', sourcing: 'sourcing', offer: 'offer_ready',
  confirmed: 'confirmed',
  production: 'in_production', video: 'video_review', shipping: 'shipping',
  customs: 'customs', out_for_delivery: 'out_for_delivery', delivered: 'delivered',
};

export function stageKeyOf(managedStatus) {
  return STATUS_TO_STAGE[String(managedStatus || '').toLowerCase()] || 'received';
}

export function stageIndexOf(managedStatus) {
  const key = stageKeyOf(managedStatus);
  return Math.max(0, MANAGED_STAGES.findIndex((s) => s.key === key));
}

export const isCancelled = (managedStatus) =>
  ['cancelled', 'declined', 'rejected'].includes(String(managedStatus || '').toLowerCase());
